/**
 * 日本語 (ja) 翻译
 * --------------------------------------------------------------------
 * 仅翻译 landing 站对外可见的 marketing 文案；workspace 等内部应用层暂时
 * 走 fallback 到 en（见 i18n/index.ts 的 fallbackLocale: 'en' 逻辑）。
 *
 * 维护原则：
 *   - 仅当某 key 真的有 ja 翻译时才在此处声明，未声明的 key 自动回落 en，
 *     避免「ja 文件里堆满和 en 一模一样的字符串」这种伪翻译。
 *   - keys 结构必须与 en.js 完全一致（嵌套路径相同），否则 get(key) 找不到。
 *   - 文案以 AI 草稿翻译生成，需后续人工审校（@TODO 标注未审校段落）。
 */

import { landingHomeJa } from './landing-home/ja.js';
import { landingSecondaryJa, landingSecondaryPricingJa } from './landing-secondary/ja.js';
import {
  landingUnsupportedJa,
  landingUnsupportedInviteJa,
  landingUnsupportedMiscJa,
} from './landing-unsupported/ja.js';
import { landingRemainingJa } from './landing-remaining/ja.js';
import { landingUnifiedJa } from './landing-unified/ja.js';
export const ja = {
  // ============================================================
  // 通用按钮 / 状态文案（landing & marketing 共享）
  // ============================================================
  common: {
    signIn: 'ログイン',
    signUp: '新規登録',
    error: landingUnsupportedMiscJa.common.error,
  },

  // ============================================================
  // V3 Landing 页面（index3）
  // 文案 keys 与 en.js 的 landing_v3 命名空间镜像
  // 翻訳ステータス：AI ドラフト（要レビュー）@TODO ネイティブチェック
  // ============================================================
  landing_v3: {
    seo: {
      title: 'Veeso AI | コンテンツをカルーセル・広告・スライドに',
      description:
        'ドキュメント・ブリーフ・スプレッドシートを貼り付けるだけ。投稿・提案・販売・配信に使える、編集可能なデザインに。',
      keywords:
        'AIデザイン, コンテンツドリブン, SNSビジュアル, 中小企業マーケティング, オートレイアウト, ビジュアルコンテンツツール',
    },
    nav: {
      use_case: '活用事例',
      pricing: '料金',
      log_in: 'ログイン',
      start_free: '無料ではじめる',
      workspace: 'ワークスペース',
      toggle_menu_aria: 'メニューを切り替え',
      close_menu_aria: 'メニューを閉じる',
    },
    hero: {
      title_part1: 'あなたのコンテンツを',
      title_emphasis: 'カルーセル・広告・スライドに。',
      title_emphasis_prefix: 'カルーセル・広告・',
      title_emphasis_word: 'スライド',
      title_emphasis_suffix: 'に。',
      subtitle_line1: 'ドキュメント・ブリーフ・スプレッドシートを貼り付けるだけ。',
      subtitle_line2: '投稿・提案・販売・配信に使える、編集可能なデザインを手に入れよう。',
      subtitle_line2_lead: '投稿・提案・販売・配信に使える、編集可能なデザインを',
      subtitle_line2_tail: '手に入れよう。',
      cta: 'あなたのコンテンツで試す',
    },
    one_input: {
      heading: '一つの入力、複数のアウトプット。',
      subheading: '一つのコンテンツから始めて、まとめて生み出そう。',
    },
    panels: {
      customer_story_label: '導入事例 {{current}} / {{total}}',
      cta_label: '試してみる',
      stage_composer_placeholder: 'ここに内容を貼り付けて、作りたいデザインを伝えてください',
      article: {
        heading: '記事からカルーセルセットへ',
        description: 'Veeso に記事を渡すだけ。SNS 向けのページが一式そろう。',
        product_name: 'Instagram & LinkedIn カルーセル',
        demo_user_message:
          'このスタジオ特集記事を、Instagram と LinkedIn 向けの 9 枚カルーセルにして。2,340 プロジェクトの数字とダークなエディトリアル感は残して。',
        demo_ai_content:
          '了解。記事を 9 枚カルーセルにします — 1 ページ 1 アイデア、スワイプしやすいタイポ、統一感のあるダークなエディトリアルで。',
        demo_ai_plan_label: 'カルーセル構成',
        demo_ai_plan_content:
          'スライド 1 — 表紙。スライド 2-3 — サステナブル設計と事務所の理念。スライド 4-5 — 居心地に寄り添う仕事ぶりと代表作。スライド 6 — 2,340 プロジェクトの見出し。スライド 7-8 — クライアントの声と売上の成長。スライド 9 — ドリームホームで締めくくる。',
        demo_attachment_name: 'アーチファーム_スタジオ特集.md',
        demo_attachment_size: '2.3 KB · Markdown',
      },
      event_brief: {
        heading: 'イベントブリーフからプロモキットへ',
        description: 'Veeso にブリーフを渡すだけ。ポスター・投稿・招待状・バナーが揃う。',
        product_name: 'プロモキット',
        demo_user_message:
          'このブリーフから一式のプロモキットを作って — メインポスター、ソーシャルカバー、プラットフォーム広告、9:16 の招待状。',
        demo_ai_content:
          'はい。ブリーフからラインナップ、配色、タイポを揃えた 4 つの成果物を生成します。',
        demo_ai_plan_label: 'プロモキット',
        demo_ai_plan_content:
          '1080×1527 のメインポスター、1280×720 のソーシャルカバー、1280×764 の CTA 付きプラットフォーム広告、1080×1920 の招待状 — すべて Sunset Beach のブランドに統一。',
        demo_attachment_name: 'サマー・バイブス・フェス_ブリーフ.docx',
        demo_attachment_size: '12 KB · Word',
      },
      report: {
        heading: 'レポートからピッチデッキへ',
        description: 'Veeso にレポートを渡すだけ。プレゼン即用のデッキが完成。',
        product_name: 'ピッチデッキ',
        demo_user_message:
          'このウィークリーグロースレポートをプレゼン即用のデッキに圧縮して — 9 スライド、数字と Cedar Night のギフトストーリーは残して。',
        demo_ai_content:
          '了解。レポートを 9 スライドのデッキに圧縮します — 1 スライド 1 アイデア、大きな数字、経営層向け。',
        demo_ai_plan_label: 'デッキ構成',
        demo_ai_plan_content:
          'スライド 1 — 表紙。スライド 2-3 — 今週の動き＋ Cedar Night ハイライト。スライド 4 — 取り組み内容。スライド 5 — 数字。スライド 6-7 — Cedar Night ストーリー＋ なぜ起きたか。スライド 8-9 — 来週の計画＋次回更新。',
        demo_attachment_name: 'ウィークリー成長レポート_11-17.pdf',
        demo_attachment_size: '1.4 MB · PDF',
      },
    },
    layered: {
      title: 'Veeso makes designs.',
      subtitle: '画像は行き止まり。デザインは編集し続けられるファイル。',
      subtitle_line1: '画像は行き止まり。',
      subtitle_line2: 'デザインは編集し続けられるファイル。',
      tab: {
        change_text: 'テキストを変更',
        swap_visuals: 'ビジュアルを差し替え',
        layered_file: 'レイヤード ファイル',
        export: '書き出し PPTX, PDF',
        resize_formats: 'サイズを変更',
      },
      mobile_hint: {
        edit: 'ポスター上で見出しやフォントをそのまま編集。',
        swap: '同じブリーフから別ビジュアルを生成。',
        layered: '実際のデザインファイルのように要素を移動・レイヤー化。',
        export: 'PPTX・PDF などへ書き出し。',
      },
      export_button: '書き出し',
      export_menu: {
        title: '書き出し',
        format: '形式',
        resolution: '解像度',
        file_name: 'ファイル名',
        file_name_value: 'veeso-Veeso AI Launch',
        action: '書き出し',
      },
      composer_placeholder: '今、何をデザインしますか？',
      chat: {
        swap_user_prompt: '拡張版デザイン 2',
        swap_ai_reply: '了解です。同じラインナップのまま、2 つの拡張方向を提示します。',
        swap_core_concept_body:
          '同じラインナップ、別 OS。JOEL MULL × UMFANG を CRT ターミナル美学へ —— モノスペース書体、ネオン グリーンの [ SYS.DATE ] や > RUN_PROGRAM 系タグ、「SAKE / SAKE / SAKE」の RGB グリッチ。ナイトレイヴのテンションはそのまま、フライヤーは海賊ラジオのファームウェアのように読める。',
      },
    },
    layered_brief: {
      brief_intro: 'では、ここから直接デザインしましょう。これがブリーフです。',
      core_concept: 'コア コンセプト',
      brief_body:
        '一つの強いアイデアで攻めましょう。カウントダウンを軸にしたテーマと、ヒーロー ビジュアル一点 + 「Build in 24 Hours.」のような切れのある見出し。配色はミニマルに（鮮やかなアクセント 1 色 + 黒/白）、スピード感とコラボを示す幾何モチーフを添えて。',
      brief_prompt_demo: '新商品のポストを油絵風でデザインして。X（Twitter）に投稿します。',
      composer_placeholder: '何をデザインしますか？',
      send_aria: '送信',
    },
    canvas: {
      untitled_design: '無題のデザイン',
      composer_placeholder: '試しに何か入力してみてください…',
      share_to: 'シェア先',
      copy_link: 'リンクをコピー',
      collapse_sidebar: 'サイドバーを閉じる',
      expand_sidebar: 'サイドバーを開く',
      preview_unavailable: 'プレビューを表示できません',
    },
    cta: {
      title_before_emphasis: 'さあ',
      title_emphasis: '何かを',
      title_after_emphasis: 'コンテンツから作ろう。',
      subtitle: '無料で始められます。プロンプト不要。デザインスキル不要。',
      placeholder: '今、何をデザインしますか？',
      generate: '作成',
      generating: '作成中…',
      add_attachment_aria: '添付を追加',
      presets: {
        categories: {
          presentation: 'プレゼンテーション',
          carousel: 'カルーセル',
          long_form: 'ロングフォームコンテンツ',
          event_poster: 'イベントポスター',
        },
        presentation: {
          ai_design_intro: {
            label: 'AIデザインツール紹介',
            prompt:
              'AIデザインツール向けの10枚構成プレゼンテーションを作成して。対象は中小企業のマーケティングチーム。製品紹介デックとして使う。ユースケース、コア機能、生産性向上を強調。プロフェッショナルでモダン、テック感のあるスタイル。',
          },
          startup_pitch: {
            label: 'スタートアップピッチ',
            prompt:
              'フリーランスデザイナー向けAIマーケットプレイスツールのスタートアップピッチデックを作成して。対象は投資家。市場機会、プロダクトソリューション、ビジネスモデル、成長戦略を強調。約12枚。',
          },
          bootcamp_intro: {
            label: 'コース紹介',
            prompt:
              'AI初心者ブートキャンプのコース紹介プレゼンテーションを作成して。対象は若手ビジネスパーソン。学習成果、カリキュラム、対象者、受講理由を強調。明快で信頼感があり、親しみやすいスタイル。',
          },
          smartwatch_launch: {
            label: 'プロダクトローンチ',
            prompt:
              'ミニマリストなスマートウォッチのプロダクトローンチデックを作成して。対象はメディアと潜在ユーザー。デザイン哲学、コア機能、利用シーン、価格を強調。プレミアムで抑制的、モダンなスタイル。',
          },
        },
        carousel: {
          xhs_ai_productivity: {
            label: 'AIで仕事効率化',
            prompt:
              '小红书向け8ページカルーセル「普通の人がAIで仕事効率を上げる方法」を作成して。対象は若手ビジネスパーソン。最初の2ページは引き込み、実践的な方法とツールを段階的に説明。明快で若々しく、実用的なスタイル。',
          },
          linkedin_ai_marketing: {
            label: 'AI×コンテンツマーケ',
            prompt:
              'LinkedIn向け7ページカルーセル「AIがコンテンツマーケのワークフローをどう変えるか」を作成して。対象はB2Bマーケティングチーム。トレンド、事例、実行可能な提案を強調。プロフェッショナルで抑制的、ビジネス向けスタイル。',
          },
          instagram_scheduling_app: {
            label: 'AIスケジュールアプリ',
            prompt:
              'Instagram向け6ページカルーセル、ナレッジワーカー向けAIスケジュールアプリの紹介。痛点、コア機能、利用シーン、生産性向上を強調。クリーンでモダン、テック感のあるスタイル。',
          },
          sleep_education: {
            label: '睡眠と健康',
            prompt:
              '「なぜ睡眠が思っている以上に重要か」についての教育カルーセルを8ページ作成して。対象は一般読者。内容はわかりやすく、ビジュアルはクリーンで信頼感のあるスタイル。医療広告のような見た目は避ける。',
          },
        },
        long_form: {
          desk_lamp_detail: {
            label: 'スマートデスクライト',
            prompt:
              'スマートデスクライトの商品詳細ページを作成して。対象は在宅ワーカーと学生の保護者。商品価値の理解と購入意欲の向上が目的。痛点、セールスポイント、利用シーン、仕様、購入理由を含める。アイケア、調光、ミニマルデザイン、在宅オフィス使用を強調。クリーンでプレミアム、コンバージョン志向のスタイル。',
          },
          ai_course_landing: {
            label: 'AIコースLP',
            prompt:
              'AI初心者コースのランディングページを作成して。対象は若手ビジネスパーソン。申込促進が目的。対象者、学習成果、カリキュラム、講師の信頼性、申込情報を含める。わかりやすく実践的で仕事効率向上に役立つことを強調。プロフェッショナルで明快、信頼感のあるスタイル。',
          },
          tokyo_travel_guide: {
            label: '東京3日間ガイド',
            prompt:
              '初来日者向け東京3日間のロングフォーム旅行ガイドを作成して。保存・シェアしたくなることが目的。行程、おすすめスポット、交通、予算のコツ、注意事項を含める。クリーンで読みやすい、旅行向けスタイル。',
          },
          back_pain_education: {
            label: '腰痛の原因と対策',
            prompt:
              '「なぜ長時間座ると腰痛になるのか」についての医療教育ロングフォームページを作成して。対象はオフィスワーカー。原因の理解と改善方法の把握が目的。症状、原因、リスク、日常の改善法、注意点を含める。プロフェッショナルで信頼感のあるスタイル。誇大なマーケティング表現は避ける。',
          },
        },
        event_poster: {
          ai_tool_launch: {
            label: 'AIツールローンチ',
            prompt:
              'AIツールローンチイベントの Event Poster を作成して。テーマは「一文から編集可能なデザインを生成」。対象はコンテンツクリエイターとマーケティングチーム。SNSプロモーション用。開催時間、プロダクト価値、登録方法を強調。予約を促す。プレミアムでテック感、安っぽいSF感は避ける。',
          },
          designer_salon: {
            label: 'デザイナーサロン',
            prompt:
              'オフラインのデザイナーサロン Event Poster を作成して。テーマは「AI時代のデザインワークフロー」。対象はプロダクトデザイナーとブランドデザイナー。小红书とWeChat Moments用。トピック、登壇者、時間、場所、QRコードを強調。抑制的でプロフェッショナル、デザイン主導のスタイル。',
          },
          coffee_tasting: {
            label: 'コーヒー試飲会',
            prompt:
              'カフェの週末イベント Event Poster を作成して。新ドリンク試飲会、対象は近隣の若年消費者。店内掲示とSNS用。時間、場所、特典、来店CTAを強調。温かみのあるモダン、ライフスタイル志向のスタイル。',
          },
          gym_opening: {
            label: 'ジムオープン',
            prompt:
              'ジムオープンキャンペーンの Event Poster を作成して。対象は近隣のオフィスワーカーとフィットネス初心者。SNSとオフライン用。期間限定オファー、体験レッスン、申込方法を強調。エネルギッシュで明快、コンバージョン志向のスタイル。',
          },
        },
      },
    },
    footer: {
      group_design_tools: 'デザインツール',
      group_company: '会社情報',
      group_resources: 'リソース',
      group_connect: 'コネクト',
      company: {
        about: '概要',
        contact: 'お問い合わせ',
        terms: '利用規約',
        privacy: 'プライバシー',
        cookie_policy: 'Cookie ポリシー',
      },
      resources: {
        help: 'ヘルプ',
        solutions: 'ソリューション',
        tools: 'ツール',
        learn: 'ガイド',
        blog: 'ブログ',
        spotlight: 'スポットライト',
      },
    },
  },

  // ============================================================
  // Pricing — see docs/landing-secondary-i18n-source-ja.md §5
  // ============================================================
  pricing: {
    ...landingSecondaryPricingJa,
    breadcrumb: landingUnsupportedMiscJa.pricing.breadcrumb,
  },

  invite: landingUnsupportedInviteJa,

  languageSwitcher: landingUnsupportedMiscJa.languageSwitcher,

  // ============================================================
  // Marketing landing pages（about / contact / auth 系）
  // 翻訳ステータス：AI ドラフト（要レビュー）@TODO ネイティブチェック
  // ============================================================
  landingPages: {
    about: {
      seo: {
        title: '会社概要｜AI でデザインプロセスを再発明する',
        description:
          'Veeso AI のミッションを知る — コンテンツ in、デザイン out。アウトプットだけでなく、制作プロセスそのものを変える。',
        keywords:
          'Veeso AI 会社概要, コンテンツ in デザイン out, クリエイティブ自動化, AI デザイン, デザイン効率化, コンテンツ起点デザイン',
        ogTitle: '会社概要｜デザインの作り方を変える',
      },
      hero: {
        // \n で行を区切る。各語が word_blur stagger アニメで順次フェードイン。
        title: '想像力を\n増幅する\nツールを作る。',
      },
      mission: {
        srHeading: 'ミッション',
        // <span class="text-black/90"> でハイライトする短句を囲む。
        body: 'Veeso AI は、<span class="text-black/90"> 知的な創造システム</span>を作るというミッションのもと、<span class="text-black/90"> ロジック・美意識・コンピュテーション </span> を融合させ、デザイナーだけでなく <span class="text-black/90"> 構造的で美しい </span> コンテンツを誰もが生み出せる世界を目指しています。',
      },
      values: {
        heading: 'バリュー',
        items: {
          imagination: {
            title: '想像力',
            description: '想像力を増幅するツールを作り、刺激を与える。',
          },
          simplicity: {
            title: 'シンプルさ',
            description: '異なる概念を繋ぎ、調和して動くようにする。',
          },
          personalization: {
            title: 'パーソナライズ',
            description: 'あなたのデザインを、あなたの思いどおりに。',
          },
          designForEveryone: {
            title: 'すべての人にデザインを',
            description: 'すべての人のためのツールを作る。',
          },
        },
      },
    },
    contact: {
      seo: {
        title: 'お問い合わせ｜Veeso AI サポート & セールス',
        description:
          '製品サポート、セールス、提携、報道機関のお問い合わせは Veeso AI チームへ。クリエイターと中小企業の「コンテンツからのデザイン」を加速します。',
        keywords:
          'Veeso AI サポート, Veeso 連絡, AI デザイン ヘルプ, セールス問い合わせ, パートナー問い合わせ, クリエイターサポート, 中小企業デザインツール',
      },
      hero: {
        title: 'お問い合わせ',
        subtitle: 'お気軽にご連絡ください。チーム一同、お待ちしています。',
      },
      options: {
        emailSupport: {
          title: 'メールサポート',
          description: 'ご質問やお困りごとがあれば、メールでお送りください。折り返しご連絡します。',
          action: 'メールを送る',
        },
      },
      faq: {
        heading: 'よくある質問',
        discordFallback: 'Discord コミュニティに参加',
        items: {
          responseTime: {
            question: '回答までの目安時間は？',
            answer:
              '営業日（月〜金）の 24〜48 時間以内のご回答を目指しています。Pro / Ultra プランの方は優先サポートをご利用いただけます。',
          },
          supportTypes: {
            question: 'どのようなサポート方法がありますか？',
            answerBefore: 'support@veeso.ai のメールサポートに加え、',
            discordText: 'Discord コミュニティ',
            answerAfter:
              ' もご利用いただけます。メールでは詳細な技術サポートとリンクの共有が可能、Discord ではリアルタイムのやり取りとピアサポートが可能です。',
          },
          reportBug: {
            question: 'バグの報告方法は？',
            answerBefore:
              'バグを発見された場合は、再現手順とスクリーンショットを添えて support@veeso.ai までメールください。または ',
            discordText: 'Discord コミュニティ',
            answerAfter: ' でもご報告いただけます（より早い対応が期待できます）。',
          },
          hiring: {
            question: '採用していますか？参加方法は？',
            answer: 'hi@veeso.ai までお気軽にご連絡ください。',
          },
        },
      },
    },
    auth: {
      common: {
        email: 'メールアドレス',
        password: 'パスワード',
        verificationCode: '認証コード',
        sendCode: 'コードを送信',
        countdownSeconds: '{{count}}秒',
        or: 'または',
        togglePasswordVisibility: 'パスワード表示の切り替え',
        emailRequired: 'メールアドレスを入力してください',
        emailInvalid: '有効なメールアドレスを入力してください',
        emailPasswordRequired: 'メールアドレスとパスワードを入力してください',
        passwordRequired: 'パスワードを入力してください',
        passwordMinLength: 'パスワードは 6 文字以上で入力してください',
        verificationCodeRequired: '認証コードを入力してください',
        sendCodeFailed: '認証コードの送信に失敗しました',
        invalidCredentials: 'メールアドレスまたはパスワードが正しくありません',
        genericError: '問題が発生しました。もう一度お試しください。',
        forgotPassword: 'パスワードをお忘れですか？',
        noAccount: 'アカウントをお持ちでないですか？',
        hasAccount: 'すでにアカウントをお持ちですか？',
        createAccount: 'アカウントを作成',
        logIn: 'ログイン',
        termsPrefix: '続行することで、以下に同意したことになります：',
        termsOfService: '利用規約',
        and: 'および',
        privacyPolicy: 'プライバシーポリシー',
      },
      login: {
        seoDocumentTitle: 'ログイン - Veeso AI',
        seoTitle: 'ログイン - Veeso AI',
        seoDescription:
          'Veeso AI アカウントにログインして、見栄えのするデザイン制作を始めましょう。',
        title: 'ログイン',
        submitting: 'ログイン中...',
        submit: 'ログイン',
      },
      signup: {
        seoDocumentTitle: '新規登録 - Veeso AI',
        seoTitle: '新規登録 - Veeso AI',
        seoDescription: 'Veeso AI アカウントを作成して、見栄えのするデザイン制作を始めましょう。',
        title: '新規登録',
        submitting: 'アカウント作成中...',
        submit: 'アカウントを作成',
      },
      forgot: {
        seoDocumentTitle: 'パスワード再設定 - Veeso AI',
        seoTitle: 'パスワード再設定 - Veeso AI',
        seoDescription: 'Veeso AI アカウントのパスワードを再設定します。',
        titleInitial: 'パスワード再設定',
        titleCodeSent: '新しいパスワード',
        titleSuccess: 'パスワードを再設定しました',
        successMessage: 'パスワードを再設定しました。新しいパスワードでログインできます。',
        backToLogin: 'ログインに戻る',
        newPassword: '新しいパスワード',
        confirmPassword: 'パスワード（確認）',
        resend: '再送',
        submittingReset: '再設定中...',
        submittingSend: '送信中...',
        submitReset: 'パスワードを再設定',
        newPasswordRequired: '新しいパスワードを入力してください',
        passwordsMismatch: 'パスワードが一致しません',
        resetFailed: 'パスワード再設定に失敗しました',
        rememberPassword: 'パスワードを思い出しましたか？',
      },
      modal: {
        titleLogin: 'ログイン',
        titleSignup: '新規登録',
        descriptionLogin: 'おかえりなさい — Veeso AI へ',
        descriptionSignup: 'Veeso AI のアカウントを作成',
        continueWithEmail: 'メールアドレスで続行',
        back: '戻る',
        sending: '送信中...',
        registrationFailed: 'アカウント登録に失敗しました',
      },
      google: {
        continue: 'Google で続行',
        connecting: '接続中...',
      },
      callback: landingUnsupportedJa.auth.callback,
      loggedIn: {
        welcome: 'おかえりなさい！',
        currentlyLoggedIn: '現在ログイン中のアカウント',
        goToWorkspace: 'ワークスペースへ',
        signOut: 'ログアウト',
      },
    },

    /** Secondary pages — see docs/landing-secondary-i18n-source-ja.md */
    ...landingSecondaryJa,
    articleDetail: landingRemainingJa.articleDetail,
    paymentSuccess: landingUnsupportedJa.paymentSuccess,
    error: landingUnsupportedJa.error,
    blog: {
      ...landingUnsupportedJa.blog,
      index: {
        ...landingUnsupportedJa.blog.index,
        ...landingRemainingJa.blog.index,
        hero: {
          ...landingUnsupportedJa.blog.index.hero,
          ...landingRemainingJa.blog.index.hero,
        },
        seo: {
          ...landingUnsupportedJa.blog.index.seo,
          ...landingRemainingJa.blog.index.seo,
        },
        faq: {
          ...landingUnsupportedJa.blog.index.faq,
          ...landingRemainingJa.blog.index.faq,
        },
      },
      detail: landingRemainingJa.blog.detail,
    },
    spotlight: {
      ...landingUnsupportedJa.spotlight,
      index: {
        ...landingUnsupportedJa.spotlight.index,
        ...landingRemainingJa.spotlight.index,
        hero: {
          ...landingUnsupportedJa.spotlight.index.hero,
          ...landingRemainingJa.spotlight.index.hero,
        },
        seo: {
          ...landingUnsupportedJa.spotlight.index.seo,
          ...landingRemainingJa.spotlight.index.seo,
        },
        faq: {
          ...landingUnsupportedJa.spotlight.index.faq,
          ...landingRemainingJa.spotlight.index.faq,
        },
      },
      detail: landingRemainingJa.spotlight.detail,
    },
    aiToolsIndex: {
      ...landingSecondaryJa.aiToolsIndex,
      ...landingUnsupportedJa.aiToolsIndex,
      card: {
        ...landingSecondaryJa.aiToolsIndex?.card,
        ...landingUnsupportedJa.aiToolsIndex?.card,
      },
      seo_content: {
        ...landingSecondaryJa.aiToolsIndex?.seo_content,
        ...landingUnsupportedJa.aiToolsIndex?.seo_content,
        ...landingRemainingJa.aiToolsIndex.seo_content,
      },
    },
    aiToolsDetail: landingRemainingJa.aiToolsDetail,
    solutions: {
      ...landingSecondaryJa.solutions,
      slug: landingRemainingJa.solutions.slug,
      detail: landingUnifiedJa.solutions.detail,
    },
    legal: {
      ...landingSecondaryJa.legal,
      privacy: {
        ...landingSecondaryJa.legal.privacy,
        page: {
          ...landingSecondaryJa.legal.privacy.page,
          ...landingUnsupportedJa.legal.privacy.page,
        },
      },
      cookie: {
        ...landingSecondaryJa.legal.cookie,
        page: {
          ...landingSecondaryJa.legal.cookie.page,
          ...landingUnsupportedJa.legal.cookie.page,
        },
      },
    },
  },

  /** Homepage `/` — see docs/landing-home-i18n-source-en.md */
  landing_home: landingHomeJa,
};
